import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './_styles_/ZoneDetailTab.css';
import { zonePropertiesType, zoneType } from '../../types';
import ReadonlyField from '../fields/ReadonlyField';
import ReadonlyList from '../fields/ReadonlyList';
import ExpandedListTab from '../ExpandedListTab';
import Privileges from '../../utils/Privileges';
import {
    codeValidator,
    nameValidator,
    ZONE_CODE_MAX_LETTERS,
    ZONE_NAME_MAX_LETTERS,
} from '../../validators/zones';
import useExtendedState from '../../hooks/useExtendedState';
import PrivilegedTextField from '../fields/privileged/PrivilegedTextField';
import useMultiValidator from '../../hooks/useMultiValidator';
import ApplyChangesButton from '../buttons/ApplyChangesButton';
import { Privileged } from '../../hocs/withPrivileges';
import useExtendedMutation from '../../hooks/useExtendedMutation';
import { ALL_ZONES_QUERY_NAME, TOGGLE_ZONE_ACTIVATION, UPDATE_ZONE } from '../../graphql/zones';
import PrivilegedFlagIconField from '../fields/privileged/PrivilegedFlagIconField';
import PrivilegedSelectField from '../fields/privileged/PrivilegedSelectField';
import ActivationButton from '../buttons/ActivationButton';
import InfoPanel from '../InfoPanel';
import { transformZoneBlockers, ZONE_BLOCKER_AUCTION_RUNNING_OR_CLOSED } from '../blockers/ZoneBlockers';
import { compositeValidator, wafValidator } from '../../hooks/validationSupport';

ZoneDetailTab.propTypes = {
    zone: zoneType.isRequired,
    zoneProps: zonePropertiesType.isRequired,
};

function ZoneDetailTab(props) {
    const {
        zone,
        zoneProps,
    } = props;
    const {
        availableClearingHouses,
        availableCurrencies,
    } = zoneProps;
    const { t } = useTranslation();
    const codeState = useExtendedState(zone.code);
    const nameState = useExtendedState(zone.name);
    const flagCodeState = useExtendedState(zone.flagCode);
    const clearingHouseCodeState = useExtendedState(zone.clearingHouse.code);
    const currencyCodeState = useExtendedState(zone.currency.code);
    const activeState = useExtendedState(zone.active);
    const { validators, isValid } = useMultiValidator(true, {
        name: compositeValidator(nameValidator(t), wafValidator(t)),
        code: compositeValidator(codeValidator(t), wafValidator(t)),
    });
    const isChanged = codeState.isChanged || nameState.isChanged || flagCodeState.isChanged || clearingHouseCodeState.isChanged || currencyCodeState.isChanged;
    const isChangedPromise = isChanged ? Promise.resolve() : Promise.reject();
    const [isDisabled, setIsDisabled] = useState(true);
    Promise.all([isChangedPromise, isValid()])
        .then(() => setIsDisabled(false))
        .catch(() => setIsDisabled(true));

    const isActivationButtonDisabled = activeState.value && zone.deactivationBlockers?.length > 0;
    let toggleZoneActivationConfirmMessageKey = 'zones.confirm-activate-zone';
    let toggleZoneActivationFailureMessageKey = 'zones.notification.zone-activate-failure';
    if (activeState.value) {
        toggleZoneActivationConfirmMessageKey = 'zones.confirm-deactivate-zone';
        toggleZoneActivationFailureMessageKey = 'zones.notification.zone-deactivate-failure';
    }
    const statesToResetOnSuccess = [codeState, nameState, flagCodeState, clearingHouseCodeState, currencyCodeState, activeState];
    const toggleZoneActivationMutation = useExtendedMutation(TOGGLE_ZONE_ACTIVATION, {
        variables: { id: zone.id },
        successCallback: () => {
            activeState.changeInitialState(!activeState.value);
            activeState.reset();
        },
        successMessageCreator: ({ toggleZoneActivation: { active: updatedActive, name: updatedName, code: updatedCode } }) => {
            let toggleZoneActivationSuccessMessageKey = 'zones.notification.zone-deactivate-success';
            if (updatedActive) {
                toggleZoneActivationSuccessMessageKey = 'zones.notification.zone-activate-success';
            }
            return t(toggleZoneActivationSuccessMessageKey, { name: updatedName, code: updatedCode });
        },
        failureMessageOptions: { key: toggleZoneActivationFailureMessageKey, params: { name: zone.name, code: zone.code } },
        refetchQueries: [ALL_ZONES_QUERY_NAME],
    });
    const updateZoneMutation = useExtendedMutation(UPDATE_ZONE, {
        variables: {
            id: zone.id,
            code: codeState.value,
            name: nameState.value,
            flagCode: flagCodeState.value,
            clearingHouseCode: clearingHouseCodeState.value,
            currencyCode: currencyCodeState.value,
        },
        statesToResetOnSuccess,
        successMessageOptions: { key: 'zones.notification.zone-update-success', dataProperty: 'updateZone' },
        failureMessageOptions: { key: 'zones.notification.zone-update-failure', params: { name: zone.name, code: zone.code } },
        refetchQueries: [ALL_ZONES_QUERY_NAME],
    });
    const sufficientPrivileges = [Privileges.ZONE_EDIT_ALL];
    const canModify = !zone.modificationBlockers?.includes(ZONE_BLOCKER_AUCTION_RUNNING_OR_CLOSED);
    const canModifyRestrictedFields = canModify && zone.modificationBlockers && zone.modificationBlockers.length === 0;
    return (
        <ExpandedListTab
            className="zone-detail-tab"
            title={t('zones.zone-detail')}
        >
            <div className="zone-detail-tab--readonly-fields">
                <ReadonlyField
                    label={t('zones.active')}
                    value={activeState.value ? t('misc.yes') : t('misc.no')}
                    className="zone-detail-tab--fields--active"
                />
            </div>
            <div className="zone-detail-tab--fields">
                <PrivilegedTextField
                    sufficientPrivileges={canModifyRestrictedFields ? sufficientPrivileges : []}
                    className="zone-detail-tab--fields--code"
                    inputId="zone-detail-tab--fields--code"
                    label={t('zones.code')}
                    state={codeState}
                    validator={validators.code}
                    maxLength={ZONE_CODE_MAX_LETTERS}
                    placeholder={t('zones.placeholder.code')}
                    isRequired
                />
                <PrivilegedTextField
                    sufficientPrivileges={canModify ? sufficientPrivileges : []}
                    className="zone-detail-tab--fields--name"
                    inputId="zone-detail-tab--fields--name"
                    label={t('zones.name')}
                    state={nameState}
                    validator={validators.name}
                    maxLength={ZONE_NAME_MAX_LETTERS}
                    placeholder={t('zones.placeholder.name')}
                    isRequired
                />
                <PrivilegedFlagIconField
                    sufficientPrivileges={canModify ? sufficientPrivileges : []}
                    label={t('zones.flag')}
                    state={flagCodeState}
                    inputId="zone-detail-tab--fields--flag"
                    className="zone-detail-tab--fields--flag"
                />
                <PrivilegedSelectField
                    sufficientPrivileges={canModify ? sufficientPrivileges : []}
                    label={t('zones.clearing-house')}
                    state={clearingHouseCodeState}
                    options={availableClearingHouses.map((availableClearingHouse) => (
                        <option value={availableClearingHouse.code} key={availableClearingHouse.code}>{availableClearingHouse.name} ({availableClearingHouse.code})</option>
                    ))}
                    className="zone-detail-tab--fields--clearing-house"
                    inputId="zone-detail-tab--fields--clearing-house"
                    isRequired
                />
                <PrivilegedSelectField
                    sufficientPrivileges={canModifyRestrictedFields ? sufficientPrivileges : []}
                    label={t('zones.currency')}
                    state={currencyCodeState}
                    options={availableCurrencies.map((availableCurrency) => (
                        <option value={availableCurrency.code} key={availableCurrency.code}>{availableCurrency.name} ({availableCurrency.code})</option>
                    ))}
                    className="zone-detail-tab--fields--currency"
                    inputId="zone-detail-tab--fields--currency"
                    isRequired
                />
            </div>
            <ReadonlyList
                className="zone-detail-tab--fields--assigned-members"
                label={t('zones.assigned-members')}
                values={zone.assignedMembers.map((assignedMember) => `${assignedMember.memberId} (${assignedMember.name})`)}
            />
            <ReadonlyList
                className="zone-detail-tab--fields--forbidden-users"
                label={t('zones.forbidden-users')}
                values={zone.forbiddenUsers.map((forbiddenUser) => `${forbiddenUser.racf} (${forbiddenUser.name})`)}
            />
            <Privileged sufficientPrivileges={sufficientPrivileges}>
                <div className="rich-list-row-expanded--buttons">
                    <div className="rich-list-row-expanded--buttons--left">
                        <ApplyChangesButton
                            extendedMutation={updateZoneMutation}
                            disabled={isDisabled || !canModify}
                            confirmationMessage={t('zones.confirm-update-zone', { name: zone.name, code: zone.code })}
                        />
                    </div>
                    <div className="rich-list-row-expanded--buttons--middle">
                        <ActivationButton
                            isActive={activeState.value}
                            disabled={isActivationButtonDisabled}
                            extendedMutation={toggleZoneActivationMutation}
                            confirmationMessage={t(toggleZoneActivationConfirmMessageKey, { name: zone.name, code: zone.code })}
                        />
                    </div>
                    <div className="rich-list-row-expanded--buttons--right" />
                </div>
                {zone.deactivationBlockers?.map((deactivationBlocker) => (
                    <InfoPanel
                        key={deactivationBlocker}
                        message={transformZoneBlockers(t, deactivationBlocker)}
                    />
                ))}
                {zone.modificationBlockers?.map((modificationBlocker) => (
                    <InfoPanel
                        key={modificationBlocker}
                        message={transformZoneBlockers(t, modificationBlocker)}
                    />
                ))}
            </Privileged>
        </ExpandedListTab>
    );
}

export default ZoneDetailTab;
